import { useEffect, useState, type FormEvent, useMemo } from 'react';
import { DollarSign, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useClients } from '@/hooks/useClients';
import { useInvoices, useRecordPayment } from '@/hooks/useFinancials';
import {
  PAYMENT_METHOD_LABELS, type PaymentMethod,
} from '@/types/financials';
import { formatCurrency } from '@/lib/utils';

type Props = {
  open: boolean;
  onClose: () => void;
  defaultClientId?: string;
  defaultInvoiceId?: string;
};

function today() { return new Date().toISOString().slice(0, 10); }

export function RecordPaymentModal({ open, onClose, defaultClientId, defaultInvoiceId }: Props) {
  const { data: clients } = useClients({ limit: 500 });
  const record = useRecordPayment();

  const [clientId, setClientId] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [method, setMethod] = useState<PaymentMethod>('check');
  const [reference, setReference] = useState('');
  const [receivedDate, setReceivedDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [applications, setApplications] = useState<Record<string, number>>({}); // invoiceId → amount
  const [error, setError] = useState<string | null>(null);

  // Open invoices for the selected client — only draft excluded, void excluded
  const { data: invoices } = useInvoices(clientId ? { clientId } : {});
  const openInvoices = useMemo(
    () => (invoices ?? []).filter((inv) =>
      ['sent', 'partial', 'past_due'].includes(inv.status) &&
      (inv.totals?.balanceDue ?? 0) > 0,
    ),
    [invoices],
  );

  useEffect(() => {
    if (open) {
      setClientId(defaultClientId ?? '');
      setAmount(0);
      setMethod('check');
      setReference('');
      setReceivedDate(today());
      setNotes('');
      setApplications(defaultInvoiceId ? { [defaultInvoiceId]: 0 } : {});
      setError(null);
    }
  }, [open, defaultClientId, defaultInvoiceId]);

  // Auto-apply to oldest-first when amount or open invoices change
  useEffect(() => {
    if (amount <= 0 || openInvoices.length === 0) return;
    // Only auto-apply when the user hasn't touched applications yet
    const anyTouched = Object.values(applications).some((v) => v > 0);
    if (anyTouched) return;

    let remaining = amount;
    const applied: Record<string, number> = {};
    const sorted = [...openInvoices].sort(
      (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
    );
    for (const inv of sorted) {
      if (remaining <= 0) break;
      const chunk = Math.min(remaining, inv.totals?.balanceDue ?? 0);
      if (chunk > 0) {
        applied[inv.id] = Number(chunk.toFixed(2));
        remaining -= chunk;
      }
    }
    setApplications(applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, openInvoices.length]);

  const appliedTotal = Object.values(applications).reduce((s, v) => s + v, 0);
  const unapplied = Math.max(0, amount - appliedTotal);
  const overApplied = appliedTotal > amount + 0.001;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!clientId) { setError('Select a client.'); return; }
    if (amount <= 0) { setError('Amount must be greater than zero.'); return; }
    if (overApplied) { setError('Applied amounts exceed payment total.'); return; }

    const apps = Object.entries(applications)
      .filter(([, v]) => v > 0)
      .map(([invoiceId, amt]) => ({ invoiceId, amount: Number(amt.toFixed(2)) }));

    try {
      const result = await record.mutateAsync({
        clientId, amount, method, reference, receivedDate,
        applications: apps, notes,
      });
      toast.success(`Recorded ${result.number} — ${formatCurrency(amount)}`);
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Payment recording failed.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker="New Payment"
      title="Record a payment."
      description="Apply against open invoices. Unapplied amounts create a credit on the client's account."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={record.isPending}>Cancel</Button>
          <Button type="submit" form="pmt-form" loading={record.isPending} leftIcon={<DollarSign size={13} />}>
            Record Payment
          </Button>
        </>
      }
    >
      <form id="pmt-form" onSubmit={onSubmit} className="space-y-5 py-2">
        {/* Core payment fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Client"
            required
            value={clientId}
            onChange={(e) => { setClientId(e.target.value); setApplications({}); }}
            options={[
              { value: '', label: '— Select a client —' },
              ...(clients ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <Input
            label="Received Date"
            type="date"
            required
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
          />
          <Input
            label="Amount"
            type="number" min="0" step="0.01"
            required
            placeholder="0.00"
            value={amount || ''}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            leftIcon={<DollarSign size={14} />}
          />
          <Select
            label="Method"
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            options={Object.entries(PAYMENT_METHOD_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          />
          <Input
            label="Reference"
            placeholder={
              method === 'check' ? 'Check #' :
              method === 'wire' ? 'Wire confirmation' :
              method === 'ach' ? 'ACH trace' :
              'Reference number'
            }
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="md:col-span-2"
          />
        </div>

        {/* Apply to invoices */}
        {clientId && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="kicker">Apply to Invoices</div>
              <div className="text-xs text-text-muted tabular">
                Applied: {formatCurrency(appliedTotal)} ·{' '}
                <span className={unapplied > 0 ? 'text-warning' : 'text-text-dim'}>
                  Unapplied: {formatCurrency(unapplied)}
                </span>
              </div>
            </div>

            {openInvoices.length === 0 ? (
              <div className="border border-dashed border-border rounded-sm p-6 text-center text-sm text-text-muted">
                {clientId ? 'No open invoices for this client.' : 'Select a client to see open invoices.'}
              </div>
            ) : (
              <div className="border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-bg-subtle text-left">
                      <th className="kicker px-4 py-2 font-normal">Invoice</th>
                      <th className="kicker px-4 py-2 font-normal">Due</th>
                      <th className="kicker px-4 py-2 font-normal text-right">Balance</th>
                      <th className="kicker px-4 py-2 font-normal text-right w-36">Apply</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openInvoices.map((inv) => {
                      const bal = inv.totals?.balanceDue ?? 0;
                      const isOverdue = new Date(inv.dueDate).getTime() < Date.now();
                      return (
                        <tr key={inv.id} className="border-b border-border last:border-0">
                          <td className="px-4 py-3 font-mono text-brass-300">{inv.number}</td>
                          <td className={`px-4 py-3 tabular text-xs ${isOverdue ? 'text-danger' : 'text-text-muted'}`}>
                            {new Date(inv.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-right tabular text-text">{formatCurrency(bal)}</td>
                          <td className="px-4 py-3 text-right">
                            <input
                              type="number"
                              min="0"
                              max={bal}
                              step="0.01"
                              value={applications[inv.id] ?? ''}
                              onChange={(e) => {
                                const val = Number(e.target.value) || 0;
                                setApplications((a) => ({
                                  ...a,
                                  [inv.id]: Math.min(val, bal),
                                }));
                              }}
                              className="w-28 h-8 px-2 text-right text-sm bg-bg-subtle border border-border rounded-sm tabular focus:border-brass-400 focus:bg-bg-panel outline-none"
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {overApplied && (
          <div className="flex items-center gap-2 text-xs text-danger border-l-2 border-danger pl-3 py-1">
            <AlertCircle size={12} />
            Applied amount exceeds total payment.
          </div>
        )}

        <Textarea
          label="Notes"
          rows={2}
          placeholder="Optional"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}
